import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { UIMessage } from 'ai'
import { Link, useParams } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { useDocumentTitle } from 'usehooks-ts'
import { APP_NAME } from '@/lib/env'
import { cn } from '@/lib/utils'
import {
  PAGE_HEADER_ACTIONS_SLOT_ID,
  PAGE_HEADER_BREADCRUMB_SLOT_ID,
  PAGE_HEADER_EXTRA_SLOT_ID,
  pageHeaderDynamicTitleAtom,
  usePageHeaderConfig,
} from '@/lib/page-header'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@deepguide-ai/ui/resizable'
import { AskAiButton } from '@/components/chat/AskAiButton'
import { ChatPanel } from '@/components/chat/ChatPanel'
import MainHeader from '@/components/layout/MainHeader'
import { Main } from '@/components/main'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { useAtomValue } from 'jotai'

/**
 * Width tokens for the main content column.
 *
 * - `full`     — no max-width (default).
 * - `reading`  — max-w-5xl (~1024px), centered.
 * - `document` — max-w-3xl (~768px), centered.
 */
export type ScreenataPageWidth = 'full' | 'reading' | 'document'

const WIDTH_CLASS: Record<ScreenataPageWidth, string> = {
  full: '',
  reading: 'mx-auto w-full max-w-5xl',
  document: 'mx-auto w-full max-w-3xl',
}

/**
 * Chat layout intent for the page.
 *
 * - `'none'`  — no chat docked. If `chatConfig` is provided, the layout
 *                still mounts a closed chat column that opens on AskAi.
 * - `'full'`  — chat occupies the entire content area; main is hidden.
 *                Used by drafting / wizard surfaces.
 * - `'right'` — chat docks at a fixed width on the right; main fills the
 *                remainder. Open by default; closable.
 */
export type ScreenataChatMode = 'none' | 'full' | 'right'

export interface ScreenataChatConfig {
  api: string
  requestBody?: Record<string, unknown>
  autoStartMessage?:
    | string
    | { message: string; body?: Record<string, unknown> }
  onMessagesChange?: (messages: UIMessage[]) => void
  onStreamFinish?: () => void
  /** When true, only send the last user message (default: false). */
  sendOnlyLastMessage?: boolean
  /** Forwarded to ChatPanel — bumping forces a remount of the chat. */
  regenerateKey?: number
}

interface ScreenataPageChatActions {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
  /** True only when the page actually has a chatConfig wired. */
  hasConfig: boolean
}

const ScreenataPageChatContext = createContext<ScreenataPageChatActions | null>(
  null
)

/**
 * Returns the page's embedded-chat actions when the surrounding
 * `ScreenataPageLayout` has a `chatConfig`. Used by `AskAiButton` to
 * toggle the layout's docked chat instead of the workspace floating
 * chat.
 */
export function useScreenataPageChat(): ScreenataPageChatActions | null {
  return useContext(ScreenataPageChatContext)
}

interface ScreenataPageLayoutProps {
  title?: string
  /** Optional breadcrumb rendered before the title (e.g. a Link back to the list page) */
  breadcrumb?: React.ReactNode
  /** When true, `breadcrumb` is the full trail (no chevron + h1 title after it). */
  breadcrumbIsTerminal?: boolean
  /** Optional content rendered to the right of the title in the header bar */
  headerActions?: React.ReactNode
  /** Rendered after Ask Vera in the header bar (e.g. Exit auditor view). */
  headerTrailing?: React.ReactNode
  /** Extra content rendered inline after the title (e.g. stats text) */
  headerExtra?: React.ReactNode
  /** When set, replaces the entire MainHeader content (title, headerExtra, headerActions are ignored) */
  customHeader?: React.ReactNode
  /** Extra classes on MainHeader when `customHeader` is set (e.g. admin preview styling). */
  customHeaderClassName?: string
  /** Extra classes on the default MainHeader bar. */
  headerClassName?: string
  /** Additional className on the Main wrapper */
  mainClassName?: string
  /** Additional className on the content div inside Main */
  contentClassName?: string
  /** When true, children are rendered directly inside Main without the default p-6 wrapper */
  raw?: boolean
  /** Cap the content column width. */
  width?: ScreenataPageWidth
  /** When true, this layout owns the middle content scroll area. */
  scroll?: boolean
  scrollClassName?: string
  scrollViewClassName?: string
  /**
   * Embedded chat layout intent. Defaults to `'none'`. When `chatConfig`
   * is supplied AND chatMode is `'none'`, the layout mounts a hidden
   * chat that opens when AskAi is clicked.
   */
  chatMode?: ScreenataChatMode
  /** Configuration for the embedded ChatPanel. */
  chatConfig?: ScreenataChatConfig
  /**
   * Hide the header "Ask Vera" button. Set when the page is rendered as a
   * PREVIEW inside a panel/overlay (system-detail overlay), where a
   * page-level chat affordance doesn't apply.
   */
  hideAskAi?: boolean
  hideHeader?: boolean
  children: React.ReactNode
}

export function ScreenataPageLayout({
  title,
  breadcrumb,
  breadcrumbIsTerminal = false,
  headerActions,
  headerTrailing,
  headerExtra,
  customHeader,
  customHeaderClassName,
  headerClassName,
  mainClassName,
  contentClassName,
  raw,
  width,
  scroll,
  scrollClassName,
  scrollViewClassName,
  chatMode = 'none',
  chatConfig,
  hideAskAi = false,
  hideHeader = false,
  children,
}: ScreenataPageLayoutProps) {
  const routeHeaderConfig = usePageHeaderConfig()
  const dynamicTitle = useAtomValue(pageHeaderDynamicTitleAtom)
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const isRouteDriven = !title && !customHeader
  const shouldBypassRouteLayout =
    isRouteDriven && (!routeHeaderConfig || routeHeaderConfig.customHeader)

  const effectiveTitle = dynamicTitle ?? title ?? routeHeaderConfig?.title
  const effectiveWidth = width ?? routeHeaderConfig?.width ?? 'full'
  const effectiveRaw = raw ?? routeHeaderConfig?.pad === false
  const effectiveScroll =
    scroll ?? (isRouteDriven ? routeHeaderConfig?.scroll !== false : false)
  const hideVisibleTitle =
    isRouteDriven && routeHeaderConfig?.hideVisibleTitle === true
  // `titleHref` makes the section title a breadcrumb node on drill-down pages
  // (e.g. "Frameworks › SOC 2"). It is workspace-relative, so without a
  // workspace in the path there is nothing to resolve it against.
  const titleHref =
    routeHeaderConfig?.titleHref && workspaceId
      ? `/workspaces/${workspaceId}${routeHeaderConfig.titleHref}`
      : undefined
  const widthClass = WIDTH_CLASS[effectiveWidth]
  useDocumentTitle(
    effectiveTitle ? `${effectiveTitle} - ${APP_NAME}` : APP_NAME
  )

  // Internal open/closed state. `'full'` is always open and not
  // closable; `'right'` is open by default; `'none'` (with chatConfig)
  // is closed by default and only opens when AskAi is clicked.
  const [isChatOpen, setIsChatOpen] = useState(
    chatMode === 'full' || chatMode === 'right'
  )
  // Re-sync when the parent toggles the prop deliberately.
  useEffect(() => {
    setIsChatOpen(chatMode === 'full' || chatMode === 'right')
  }, [chatMode])

  const open = useCallback(() => setIsChatOpen(true), [])
  const close = useCallback(() => {
    if (chatMode === 'full') return
    setIsChatOpen(false)
  }, [chatMode])
  const toggle = useCallback(() => {
    if (chatMode === 'full') return
    setIsChatOpen((v) => !v)
  }, [chatMode])

  const chatActions = useMemo<ScreenataPageChatActions>(
    () => ({
      isOpen: isChatOpen,
      open,
      close,
      toggle,
      hasConfig: !!chatConfig,
    }),
    [chatConfig, close, isChatOpen, open, toggle]
  )

  if (shouldBypassRouteLayout) {
    return <>{children}</>
  }

  const header = customHeader ? (
    <MainHeader
      className={cn('!mb-0 !h-[50px] p-0 pl-5', customHeaderClassName)}
    >
      {customHeader}
    </MainHeader>
  ) : (
    <MainHeader
      leading={
        hideVisibleTitle ? <span className='hidden' aria-hidden /> : undefined
      }
      className={cn('!mb-0 !h-[50px] px-5', headerClassName)}
    >
      <div className='flex w-full min-w-0 items-center gap-2'>
        <div className={hideVisibleTitle ? 'hidden' : 'contents'}>
          {breadcrumb ? (
            breadcrumbIsTerminal ? (
              breadcrumb
            ) : (
              <>
                {breadcrumb}
                <ChevronRight
                  className='h-3.5 w-3.5 shrink-0 text-text-placeholder'
                  aria-hidden
                />
              </>
            )
          ) : (
            <>
              <div
                id={PAGE_HEADER_BREADCRUMB_SLOT_ID}
                className='peer flex items-center gap-2 empty:hidden'
              />
              <ChevronRight
                className='hidden h-3.5 w-3.5 shrink-0 text-text-placeholder peer-[:not(:empty)]:block'
                aria-hidden
              />
            </>
          )}
          {!breadcrumbIsTerminal && (
            <h1 className='font-heading text-[15px] truncate font-semibold text-foreground'>
              {titleHref ? (
                <Link
                  to={titleHref}
                  className='transition-colors hover:text-text-body'
                >
                  {effectiveTitle}
                </Link>
              ) : (
                effectiveTitle
              )}
            </h1>
          )}
        </div>
        {headerExtra ?? (
          <div
            id={PAGE_HEADER_EXTRA_SLOT_ID}
            className={cn(
              'flex items-center gap-2',
              hideVisibleTitle && 'min-w-0 flex-1 self-stretch'
            )}
          />
        )}
        <div className='ml-auto flex flex-nowrap items-center justify-end gap-2 '>
          {headerActions}
          {!headerActions && (
            <div
              id={PAGE_HEADER_ACTIONS_SLOT_ID}
              className='flex flex-nowrap items-center  justify-end gap-2'
            />
          )}
          {!isChatOpen && !hideAskAi && <AskAiButton variant='header' />}
          {headerTrailing}
        </div>
      </div>
    </MainHeader>
  )

  const mainContent = effectiveRaw ? (
    children
  ) : (
    <div className={cn('w-full space-y-6 p-6', widthClass, contentClassName)}>
      {children}
    </div>
  )

  const mainColumn = (
    <>
      {!hideHeader && header}
      {effectiveScroll ? (
        <ScrollArea
          className={cn('min-h-0 flex-1', scrollClassName)}
          viewClassName={cn(
            '[&>div]:!block',
            scrollViewClassName
          )}
        >
          <Main className={mainClassName}>{mainContent}</Main>
        </ScrollArea>
      ) : (
        <Main className={mainClassName}>{mainContent}</Main>
      )}
    </>
  )

  // No embedded chat at all — original vertical layout (header + Main).
  if (!chatConfig) {
    return (
      <ScreenataPageChatContext.Provider value={chatActions}>
        {effectiveScroll ? (
          <div className='flex h-full min-h-0 w-full flex-col overflow-hidden'>
            {mainColumn}
          </div>
        ) : (
          mainColumn
        )}
      </ScreenataPageChatContext.Provider>
    )
  }

  const showChat = isChatOpen
  const isFull = chatMode === 'full' && showChat

  // Chat panel — single mounted instance. The ChatPanel renders its
  // own header (thread name, controls); we forward `onClose` so the
  // panel's built-in × routes through the layout's `close()`.
  const chatPanel = (
    <ChatPanel
      key={chatConfig.regenerateKey}
      api={chatConfig.api}
      requestBody={chatConfig.requestBody}
      autoStartMessage={chatConfig.autoStartMessage}
      onMessagesChange={chatConfig.onMessagesChange}
      onStreamFinish={chatConfig.onStreamFinish}
      sendOnlyLastMessage={chatConfig.sendOnlyLastMessage ?? false}
      onClose={chatMode === 'full' ? undefined : close}
      hideCloseButton={chatMode === 'full'}
      className='h-full w-full'
    />
  )

  // chatMode='full' — chat occupies the entire layout root, no main
  // column, no resizable handle.
  if (isFull) {
    return (
      <ScreenataPageChatContext.Provider value={chatActions}>
        <div className='flex h-full min-h-0 w-full overflow-hidden'>
          {chatPanel}
        </div>
      </ScreenataPageChatContext.Provider>
    )
  }

  // Chat is closed — render the page like the no-chat branch (header
  // + Main), no resizable group needed.
  if (!showChat) {
    return (
      <ScreenataPageChatContext.Provider value={chatActions}>
        {effectiveScroll ? (
          <div className='flex h-full min-h-0 w-full flex-col overflow-hidden'>
            {mainColumn}
          </div>
        ) : (
          mainColumn
        )}
      </ScreenataPageChatContext.Provider>
    )
  }

  // Chat is open in right-dock mode — horizontal resizable split.
  // Main column owns header + content; chat column is the right
  // panel. The handle between them lets the user widen / narrow the
  // chat.
  return (
    <ScreenataPageChatContext.Provider value={chatActions}>
      <ResizablePanelGroup
        direction='horizontal'
        className='h-full min-h-0 w-full'
      >
        <ResizablePanel
          defaultSize='70%'
          minSize='40%'
          className='flex min-w-0 flex-col overflow-hidden'
        >
          {mainColumn}
        </ResizablePanel>
        <ResizableHandle className='bg-border hover:bg-primary/30 w-px transition-colors' />
        <ResizablePanel
          defaultSize='30%'
          minSize='20%'
          maxSize='60%'
          className='flex min-w-0 flex-col overflow-hidden bg-card'
        >
          {chatPanel}
        </ResizablePanel>
      </ResizablePanelGroup>
    </ScreenataPageChatContext.Provider>
  )
}
